export const getJobIdFromParams = (searchParams: URLSearchParams) => {
  return (
    searchParams.get("jobId") ||
    searchParams.get("jobid") ||
    searchParams.get("id") ||
    searchParams.get("KId") ||
    ""
  );
};

export const createJobApplyUrl = (searchParams: URLSearchParams) => {
  const jobId =
    searchParams.get("jobId") ||
    searchParams.get("jobid") ||
    searchParams.get("KId");
  const id = jobId || searchParams.get("id") || "";

  return `/seeker/job-details?${jobId ? "kId=" : "id="}${id}&autoApply=true`;
};

export const makeCompanyUrl = (
  companyId: number,
  companyName: string,
  isMicrosite?: number,
  micrositeUrl?: string,
) => {
  if (isMicrosite && micrositeUrl) {
    return micrositeUrl;
  } else {
    const urlParts = [companyName, companyId, "jobs-career"];
    return `/search/${urlParts
      .join("-")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .toLowerCase()}`;
  }
};

export const SEA_SITE_CONTEXT = [
  "monstersingapore",
  "monsterphilippines",
  "monstermalaysia",
  "monsterhongkong",
];

export const formatNumberWithCommas = (strText: string) =>
  strText.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const getSalaryRange = (min: string, max: string, suffix: string) =>
  `${formatNumberWithCommas(min)} - ${formatNumberWithCommas(max)} ${suffix}`;

export const getSalary = (jobData: any, siteContext: string) => {
  const { minimumSalary, maximumSalary } = jobData;

  if (!(minimumSalary && maximumSalary)) return "";

  const LAKH = 100000;
  const {
    absoluteMonthlyValue: minMonthlyValue,
    absoluteValue: minAbsoluteValue,
    currency = "",
  } = minimumSalary;
  const {
    absoluteMonthlyValue: maxMonthlyValue,
    absoluteValue: maxAbsoluteValue,
  } = maximumSalary;

  if (
    minAbsoluteValue &&
    maxAbsoluteValue &&
    !SEA_SITE_CONTEXT.includes(siteContext)
  ) {
    if (currency === "INR") {
      const lowerLPA = +(minAbsoluteValue / LAKH).toFixed(2);
      const upperLPA = +(maxAbsoluteValue / LAKH).toFixed(2);
      return getSalaryRange(lowerLPA.toString(), upperLPA.toString(), "LPA");
    }

    return getSalaryRange(
      minAbsoluteValue.toLocaleString([]),
      maxAbsoluteValue.toLocaleString([]),
      currency,
    );
  } else if (minMonthlyValue && maxMonthlyValue) {
    const currencySuffix = currency === "INR" ? "INR/m" : `${currency}/m`;
    return getSalaryRange(
      minMonthlyValue.toLocaleString([]),
      maxMonthlyValue.toLocaleString([]),
      currencySuffix,
    );
  }
  return "";
};

export const formatLocation = (
  locations: string | locationsType[] | undefined,
) => {
  const changeLocUiName = (name: string | undefined) => {
    const nameMap: { [key: string]: string } = {
      "Bengaluru / Bangalore": "Bengaluru",
      "Bangalore / Bengaluru": "Bengaluru",
      "Hyderabad / Secunderabad, Telangana": "Hyderabad",
      "Hyderabad / Secunderabad - Telangana": "Hyderabad",
      "Gurgaon / Gurugram": "Gurugram",
      "Gurugram / Gurgaon": "Gurugram",
    };
    return nameMap[name as keyof typeof nameMap] || name;
  };

  return typeof locations === "string"
    ? locations
        .split(", ")
        .map((location) => changeLocUiName(location))
        .join(", ")
    : locations
        ?.map(
          (location) =>
            changeLocUiName(location.city) ||
            changeLocUiName(location.state) ||
            changeLocUiName(location.country),
        )
        .join(", ");
};

export const removeSomeSpecialChars = (
  inputString: string,
  specialCharacter: string,
) => {
  if (!inputString || inputString.length === 0) {
    return "";
  }
  try {
    inputString = inputString.replaceAll("\r", "<br>");
    inputString = inputString.replaceAll('"', "'");
    if (specialCharacter && specialCharacter.length > 0) {
      for (let i = 0; i < specialCharacter.length; i++) {
        const regex = new RegExp(`\\${specialCharacter.charAt(i)}`, "g");
        inputString = inputString.replaceAll(regex, "");
      }
    }
  } catch (error) {
    console.error("Exception while removeSomeSpecialChars ", error);
  }
  return inputString;
};

export const formatUrlValue = (...values: string[]) => {
  if (values !== null && values !== undefined) {
    const trimmedValues = values.map((value) =>
      value.replace(/\bJob\b/g, "").trim(),
    );
    const url = trimmedValues.join("-");
    let finalUrl = removeSomeSpecialChars(url, "'{}().")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .toLowerCase();
    finalUrl = finalUrl
      .replace(/-jobs-jobs/g, "-jobs")
      .replace(/-jobs-job/g, "-jobs")
      .replace(/-job-jobs/g, "-jobs")
      .replace(/-job-job/g, "-jobs");
    return finalUrl;
  }
  return "";
};

export const formatSkills = (
  skills: string | string[] | sKillType[] | undefined,
  itSkills: string | string[] | sKillType[] | undefined,
) => {
  if (!skills && !itSkills) return [];

  const updatedSkills =
    skills && Array.isArray(skills)
      ? skills.filter((skill) => {
          if (typeof skill === "string") {
            return skill.trim() !== "";
          } else if (typeof skill === "object" && skill !== null) {
            return (
              (skill.id && skill.id.trim() !== "") ||
              (skill.text && skill.text.trim() !== "")
            );
          }
          return false;
        })
      : [];

  const jobSkills =
    typeof skills === "string"
      ? skills?.split(",")
      : updatedSkills?.map((skill) =>
          typeof skill === "string"
            ? skill
            : { skill: skill?.text?.trim(), clickable: skill?.clickable },
        );

  const jobItSkills =
    typeof itSkills === "string"
      ? itSkills?.split(",")
      : itSkills?.map((skill) =>
          typeof skill === "string"
            ? skill
            : { skill: skill?.text?.trim(), clickable: skill?.clickable },
        );

  return jobSkills &&
    jobSkills?.length > 0 &&
    jobItSkills &&
    jobItSkills?.length > 0
    ? jobSkills.concat(jobItSkills)
    : jobSkills && jobSkills?.length > 0
      ? jobSkills
      : jobItSkills && jobItSkills?.length > 0
        ? jobItSkills
        : [];
};
